import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { FolderOpen, Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useRef, useState } from 'react'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { useCoursesScope } from '@/pages/CoursesHub/useCoursesScope'
import { deleteCourseAsset, getCourseAssets } from '@/services/course_assets.service'
import { toast } from '@/utils/helpers'

import CreateAssetModal from './CreateAssetModal'
import EditAssetModal from './EditAssetModal'

const TYPE_OPTIONS = ['Video', 'PDF', 'PowerPoint', 'Articulate']

/**
 * Course Asset library — backs three scopes inside the Courses hub:
 *   - /admin/courses/course-assets (RescueX global)
 *   - /accounts/:entityId/instructors/courses/course-assets (facility)
 *   - /organizations/:organizationId/courses/course-assets (org, RSCX-320)
 *
 * The page reads its scope from the URL params via useCoursesScope. In a custom
 * (facility / org) scope the management view filters to that scope's own
 * assets. On the facility side, org-shared assets still surface (the endpoint
 * unions them in) but are read-only with a "Shared" badge — owned + edited at
 * the org.
 *
 * Delete returns 409 with the courses still referencing the asset; we surface
 * that list in the toast so the user knows where to detach first.
 */
export const CourseAssets = observer(() => {
  const { entityId, organizationId, isCustom, isFacility, scopeId, scopeArgs } = useCoursesScope()

  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [sorting, setSorting] = useState([])
  const [pagination, setPagination] = useState({ pageSize: 20, pageIndex: 0 })
  const [showCreate, setShowCreate] = useState(false)
  const [editAsset, setEditAsset] = useState(null)

  const queryClient = useQueryClient()
  // Custom scope: management view shows that scope's own assets only (the
  // AssetPicker applies the same filter so course editors only attach their
  // own assets, not RescueX globals).
  const filters = {
    ...(typeFilter ? { type: typeFilter } : {}),
    ...(isCustom ? { is_xverse_asset: false } : {}),
  }
  const queryKey = ['course-assets', scopeId, sorting, pagination, searchTerm, typeFilter]

  const { data: assets, isPending } = useQuery({
    queryKey,
    queryFn: () => getCourseAssets({ ...scopeArgs, sorting, pagination, searchTerm, filters }),
    placeholderData: keepPreviousData,
  })

  const { isPending: pendingDelete, mutate: deleteAssetMutation } = useMutation({
    mutationFn: ({ id }) => deleteCourseAsset({ ...scopeArgs, id }),
    onError: (err) => {
      // The 409 from the BE includes a "courses" array of {id, name}; the
      // axios interceptor wraps it in err.message via getErrorMessage. We
      // can't reach the structured payload from here, so let the BE detail
      // string carry the explanation.
      toast(err.message, 'error')
    },
    onSuccess: () => {
      toast('Asset deleted', 'success')
      queryClient.invalidateQueries({ queryKey: ['course-assets', scopeId] })
    },
  })

  const debounceSearch = useRef(_.debounce(setSearchTerm, 500)).current

  const visibleTypes = isCustom ? TYPE_OPTIONS.filter((t) => t !== 'Articulate') : TYPE_OPTIONS

  // An asset is shared-in (read-only here) when an org owns it but we're
  // viewing from a facility. RescueX globals are likewise not owned by a
  // facility. In the org scope the org owns its own assets, so they're editable.
  const isOwned = (row) => {
    if (row.isXverseAsset) return !isCustom
    if (isFacility && row.organization) return false
    return true
  }

  return (
    <>
      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            type="search"
            placeholder="Search assets"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />
          <Select
            value={typeFilter || 'all'}
            onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-full md:w-40">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {visibleTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(searchTerm || typeFilter) && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                setTypeFilter('')
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button onClick={() => setShowCreate(true)}>Add Asset</Button>
      </div>

      {(() => {
        const showEmptyState =
          isCustom &&
          !isPending &&
          !searchTerm &&
          !typeFilter &&
          (assets?.results?.length ?? 0) === 0
        if (showEmptyState) {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                <FolderOpen className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No Course Assets yet</h3>
                <p className="text-muted-foreground mx-auto mb-4 max-w-md text-sm">
                  A Course Asset is the file a lesson plays — a video, PDF, or presentation. Once
                  uploaded, the same asset can be reused across any number of Course Modules.
                </p>
                <Button onClick={() => setShowCreate(true)}>Upload Course Asset</Button>
              </CardContent>
            </Card>
          )
        }
        return (
          <DataTable
            columns={[
              {
                accessorKey: 'name',
                id: 'name',
                header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
                cell: ({ row }) =>
                  isFacility && row.original.organization ? (
                    <span className="flex items-center gap-2">
                      {row.original.name}
                      <Badge variant="secondary">Shared</Badge>
                    </span>
                  ) : (
                    row.original.name
                  ),
              },
              {
                accessorKey: 'type',
                id: 'type',
                header: 'Type',
                cell: ({ row }) => row.original.type,
                size: 120,
              },
              {
                id: 'fileDisplayName',
                header: 'File',
                cell: ({ row }) => row.original.fileDisplayName ?? '-',
              },
              {
                id: 'scope',
                header: 'Scope',
                cell: ({ row }) => {
                  if (row.original.isXverseAsset) return 'RescueX'
                  if (row.original.organization) return 'Organization'
                  return 'Facility'
                },
                size: 100,
              },
              {
                id: 'courseCount',
                header: 'In use',
                cell: ({ row }) => `${row.original.courseCount ?? 0} course module(s)`,
                size: 150,
              },
              {
                id: 'edit',
                cell: ({ row }) => {
                  // Only the scope's own assets are editable here — not RescueX
                  // globals, and not org-shared assets when viewed from a facility.
                  if (!isOwned(row.original)) return null
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="place-self-end"
                      aria-label={`Edit ${row.original.name}`}
                      title="Edit asset"
                      onClick={() => setEditAsset(row.original)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )
                },
                size: 60,
              },
              {
                id: 'delete',
                cell: ({ row }) => {
                  // Hide delete on assets the current scope doesn't own (RescueX
                  // globals, or org-shared assets viewed from a facility).
                  if (!isOwned(row.original)) return null
                  const inUse = (row.original.courseCount ?? 0) > 0
                  return (
                    <ConfirmButton
                      confirmContent={
                        inUse
                          ? `"${row.original.name}" is used by ${row.original.courseCount} course module(s). Detach it from those modules first.`
                          : `Delete "${row.original.name}"?`
                      }
                      confirmTitle="Delete Asset"
                      className="place-self-end"
                      variant="destructive"
                      size="sm"
                      aria-label={`Delete ${row.original.name}`}
                      title="Delete asset"
                      loading={pendingDelete}
                      disabled={inUse}
                      onClick={() => deleteAssetMutation({ id: row.original.id })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </ConfirmButton>
                  )
                },
                size: 80,
              },
            ]}
            data={assets?.results || []}
            loading={isPending}
            pagination={pagination}
            rowCount={assets?.count || 0}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
          />
        )
      })()}

      {showCreate && (
        <CreateAssetModal
          entityId={entityId}
          organizationId={organizationId}
          close={() => setShowCreate(false)}
        />
      )}
      {editAsset && (
        <EditAssetModal
          entityId={entityId}
          organizationId={organizationId}
          asset={editAsset}
          close={() => setEditAsset(null)}
        />
      )}
    </>
  )
})
